<ng-container *ngIf="{
  users: users$ | async,
  plantEventConfigurations: plantEventConfigurations$ | async,
  selectedEventModels: selectedEventModels$ | async,
  dataSource: unselectedEventModels$ | async,
} as resolver">
  <ng-container *ngIf="resolver.dataSource as eventModels; else skeleton">
    <customer-title-content
      id="notification-configuration-title"
      [title]="labels.notificationConfigurationTitle">
    </customer-title-content>
    <customer-title-content
      id="notification-configuration-users"
      [title]="labels.emailRecipients">
    </customer-title-content>
    <form
      id="users-form"
      [formGroup]="userForm">
      <mat-form-field
        *ngIf="users$ | async as users"
        subscriptSizing="dynamic"
        class="custom-mat-form-field w-full custom-border-bottom">
        <mat-select
          #devicesAttributes
          data-testid="notification-configuration-users"
          formControlName="users"
          multiple
          [placeholder]="labels.emailRecipientsPlaceholder"
          [value]="users.forEach">
          <mat-option
            *ngFor="let user of users"
            class="tw-tabular-nums tw-font-normal tw-text-base custom-option"
            [value]="user.email">
            {{ user.email }}
          </mat-option>
        </mat-select>
      </mat-form-field>
    </form>
    <ng-container *ngIf="resolver.selectedEventModels as alreadySelectedEventModels">
      <customer-title-content
        id="selected-notifications-title"
        [title]="labels.notificationConfigurationSelected">
      </customer-title-content>
      <div
        id="selected-notifications-configurations"
        class="fx-row fx-wrap fx-gap-2">
        <div
          *ngFor="let notification of alreadySelectedEventModels"
          class="text-400 text-grey-400 p-2 border-1 border-grey-400 radius-4 pointer hover"
          [attr.data-testid]="'detail-' + notification"
          [ngClass]="{ 'bg-accent-200': notification === (selectedNotificationDetail$ | async)}"
          (click)="toggleNotificationSelection(notification)">
            {{ notification | eventName: eventModelsTranslations }}
        </div>
      </div>
    </ng-container>
    <section
      *ngIf="selectedConfiguredEvent$ | async as selectedConfiguredEvent"
      id="selected-notification-detail"
      class="fx-col fx-gap-1 p-2 border-1 bg-accent-100 radius-4 border-accent-400">
      <p class="text-400 text-grey-400">
        {{ selectedConfiguredEvent.eventName | eventName: eventModelsTranslations }} ({{selectedConfiguredEvent.eventName}})
      </p>
      <p class="text-grey-400">
        {{ selectedConfiguredEvent.eventName | eventDescription: eventModelsTranslations }}
      </p>
      <form
        class="fx-col fx-gap-1 pt-2">
        <ng-container *ngTemplateOutlet="eventFormFieldsTemplate; context: { $implicit: selectedConfiguredEvent, widthClass: '', formGroup: selectedConfiguredEvent.form }">
        </ng-container>
        <div class="fx-row fx-align-center fx-justify-end fx-gap-1">
          <customer-simple-button
            type="primary"
            [label]="labels.change"
            [disabled]="!!selectedConfiguredEvent.form?.invalid"
            [action]="updateConfiguredEvent(selectedConfiguredEvent)">
          </customer-simple-button>
          <customer-simple-button
            type="warning"
            [attr.data-testid]="'remove-' + selectedConfiguredEvent.eventName"
            [label]="labels.remove"
            [action]="deleteConfiguration(selectedConfiguredEvent.eventName)">
          </customer-simple-button>
        </div>
      </form>
    </section>
    <customer-title-content
      id="notification-configuration-event-models"
      [title]="labels.notificationConfigurationAvailable">
    </customer-title-content>
    <section
      id="event-models-table"
      class="tw-w-full overflow-hidden border-1 radius-4 tw-border-grey-100">
      <azzurro-table
        tableId="notification-event-models-table"
        mode="client"
        tableLayout="fixed"
        [data$]="unselectedEventModels$"
        [columns]="columns"
        [rowExpansionTemplateByRow]="eventRowExpansionByRow"
        [rowExpansionKey]="eventRowKey"
        [showRowExpansionIcon]="false"
        [allowRowClickExpansion]="false"
        [pagination]="true"
        [paginationConfig]="paginationConfig"
        [filterEnabled]="true"
        [filterPredicate]="eventModelsFilterPredicate"
        [labels]="{
          search: labels.notificationSearchPlaceholder,
          noResults: labels.notificationTableNoResults
        }">
      </azzurro-table>
    </section>
    <section
      id="event-models-list"
      class="tw-border-grey-100 border-1 radius-4">
      <azzurro-table
        tableId="notification-event-models-list"
        mode="client"
        uiFramework="ionic"
        [data$]="unselectedEventModels$"
        [columns]="columns"
        [rowExpansionTemplateByRow]="eventRowExpansionByRow"
        [rowExpansionKey]="eventRowKey"
        [showRowExpansionIcon]="false"
        [allowRowClickExpansion]="false"
        [pagination]="true"
        [paginationConfig]="paginationConfig"
        [filterPredicate]="eventModelsFilterPredicate"
        [labels]="{
          noResults: labels.notificationTableNoResults
        }">
      </azzurro-table>
    </section>
    <div
      id="notification-configuration-actions"
      class="fx-row fx-align-center fx-justify-end fx-gap-1">
      <customer-simple-button
        data-testid="notification-configuration-save"
        type="primary"
        [label]="labels.notificationActionSave"
        [disabled]="userForm.invalid"
        [action]="saveConfiguration">
      </customer-simple-button>
      <ng-content select="[menu-options]"></ng-content>
    </div>
  </ng-container>
</ng-container>

<ng-template
  #actionTemplate
  let-row
  let-toggleRowExpansion="toggleRowExpansion"
  let-isRowExpanded="isRowExpanded">
  <div class="actions-cell">
    <button
      mat-icon-button
      type="button"
      class="action-gear"
      [attr.data-testid]="'settings-' + row.eventName"
      [matTooltip]="labels.setup"
      (click)="$event.stopPropagation(); !isRowExpanded(row) && ensureRowSelected(row); toggleRowExpansion(row)">
      <mat-icon>settings</mat-icon>
    </button>
    <mat-checkbox
      class="custom-checkbox"
      [attr.data-testid]="'checkbox-' + row.eventName"
      [checked]="row.selected$ | async"
      (click)="$event.stopPropagation()"
      (change)="row.toggleSelection()">
    </mat-checkbox>
  </div>
</ng-template>

<ng-template #actionHeaderTemplate>
  <div class="actions-header">
    <span>{{ labels.notificationTableHeaderActions }}</span>
    <mat-checkbox
      class="custom-checkbox"
      [checked]="(selectAllState$ | async) === 'checked'"
      [indeterminate]="(selectAllState$ | async) === 'indeterminate'"
      (change)="toggleAllSelection($event.checked)">
    </mat-checkbox>
  </div>
</ng-template>

<ng-template
  #eventDevicesTemplate
  let-row>
  <mat-chip-set *ngIf="row.deviceByCode; else forAllDevices">
    <mat-chip
      *ngFor="let generation of row.deviceByCode"
      style="
      --mdc-chip-elevated-container-color: var(--accent-200);
      --mdc-chip-label-text-color: var(--grey-400);">
      <span [matTooltip]="generation">
        {{ generation }}
      </span>
    </mat-chip>
  </mat-chip-set>
  <ng-template #forAllDevices>
    {{ labels.noAlarmAssociated }}
  </ng-template>
</ng-template>

<ng-template
  #eventConfigFormTemplate
  let-row>
  <form
    class="fx-col fx-gap-1 p-2"
    (click)="$event.stopPropagation()">
    <ng-container *ngTemplateOutlet="eventFormFieldsTemplate; context: { $implicit: row, widthClass: 'w-80', formGroup: row.form }">
    </ng-container>
  </form>
</ng-template>

<ng-template
  #eventFormFieldsTemplate
  let-row
  let-widthClass="widthClass"
  let-formGroup="formGroup">
  <div
    class="fx-row fx-wrap fx-gap-2"
    [formGroup]="formGroup">
    <ng-container *ngFor="let field of getEventFormFields(row)">
      <div
        class="fx-col fx-gap-1"
        [ngClass]="widthClass">
        <ng-container *ngIf="isTimeField(field); else numericFieldLabel">
          <ng-container *ngIf="getTimeFieldState(row, field) as timeState">
            <div class="fx-row fx-align-center fx-gap-1">
              <span class="tw-text-base tw-font-medium title text-grey-400">
                {{ eventFormLabels[field] || field }}
              </span>
              <span class="tw-text-xs tw-text-grey-300 tw-ml-1.5" *ngIf="timeState.minutes !== null">
                = {{ formatMinutesDurationLocalized(timeState.minutes) }}
              </span>
              <mat-icon
                class="s-4 text-grey-400"
                [matTooltip]="getEventFormTooltip(field)">
                info
              </mat-icon>
            </div>
            <div class="tw-flex tw-items-end tw-gap-2">
              <mat-form-field
                subscriptSizing="dynamic"
                class="custom-mat-form-field custom-border-bottom tw-flex-1 tw-basis-40">
                <input
                  matInput
                  type="number"
                  step="any"
                  [ngModel]="timeState.value"
                  [ngModelOptions]="{ standalone: true }"
                  (ngModelChange)="onTimeValueChange(row, field, $event)">
              </mat-form-field>
              <mat-form-field
                subscriptSizing="dynamic"
                class="custom-mat-form-field custom-border-bottom tw-w-28">
                <mat-select
                  [ngModel]="timeState.unit"
                  [ngModelOptions]="{ standalone: true }"
                  (ngModelChange)="onTimeUnitChange(row, field, $event)">
                  <mat-option
                    *ngFor="let unit of timeUnitOptions"
                    [value]="unit.value">
                    {{ unit.label | lowercase }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
            <div
              *ngIf="row.form?.get(field)?.invalid && (row.form?.get(field)?.dirty || row.form?.get(field)?.touched)"
              class="tw-text-xs tw-text-red-400 tw-mt-1">
              {{ getTimeFieldRangeError(field) }}
            </div>
          </ng-container>
        </ng-container>
        <ng-template #numericFieldLabel>
          <div class="fx-row fx-align-center fx-gap-1">
            <span class="tw-text-base tw-font-medium title text-grey-400">
              {{ eventFormLabels[field] || field }}
            </span>
            <mat-icon
              class="s-4 text-grey-400"
              [matTooltip]="getEventFormTooltip(field)">
              info
            </mat-icon>
          </div>
          <mat-form-field
            subscriptSizing="dynamic"
            class="custom-mat-form-field custom-border-bottom tw-min-w-56">
            <input
              matInput
              type="number"
              [formControlName]="field">
          </mat-form-field>
        </ng-template>
      </div>
    </ng-container>
  </div>
</ng-template>


<ng-template #skeleton>
  <ion-skeleton-text
    id="notification-configuration-title"
    animated
    class="w-50 h-14">
  </ion-skeleton-text>
  <ion-skeleton-text
    id="notification-configuration-users"
    animated
    class="w-50 h-14">
  </ion-skeleton-text>
  <ion-skeleton-text
    id="users-form"
    animated
    class="w-full h-14">
  </ion-skeleton-text>
  <section
    id="event-models-table"
    class="tw-w-full overflow-hidden border-1 tw-rounded tw-border-grey-100 fx-col">
    <div
      *ngFor="let _ of [].constructor(11); let last = last; let first = first"
      class="fx-row fx-align-center fx-justify-between fx-gap-3 border-b-1 border-grey-100 p-2">
      <ng-container *ngIf="last || first; else isRow">
        <ion-skeleton-text
          *ngIf="last"
          animated
          class="fx-g-1 h-10">
        </ion-skeleton-text>
        <ion-skeleton-text
          *ngIf="first"
          animated
          class="fx-g-1 h-20">
        </ion-skeleton-text>
      </ng-container>
      <ng-template #isRow>
        <ion-skeleton-text
          *ngFor="let _ of [].constructor(4)"
          animated
          class="fx-g-1 h-20">
        </ion-skeleton-text>
      </ng-template>
    </div>
  </section>

  <section
    id="event-models-list"
    class="tw-border-grey-100 border-1 tw-rounded-lg">
    <div
      *ngFor="let _ of [].constructor(10); let first = first"
      class="tw-flex tw-flex-col tw-p-2 tw-border-grey-100"
      [class.tw-border-t]="!first">
      <div class="tw-flex tw-justify-between tw-align-middle">
        <ion-skeleton-text
          animated
          class="w-50 h-10">
        </ion-skeleton-text>
        <ion-skeleton-text
          animated
          class="w-20 h-15">
        </ion-skeleton-text>
      </div>
      <ion-skeleton-text
        animated
        class="w-50 h-6">
      </ion-skeleton-text>
    </div>
  </section>
</ng-template>
